/**
 * StateMachine — generic, mode-agnostic driver for SDL game states.
 *
 * Mirrors UDG poker's `StateMachine`: a Map<stateId, State>, a current
 * pointer, and two entry points (`loadState`, `updateState`). The
 * machine itself doesn't know what cee-lo or craps states do — it just
 * orchestrates the lifecycle hooks. Game-mode-specific state maps come
 * from `buildStatesForMode(gameType, …)` and get installed via
 * `registerStates`.
 *
 * Lifecycle ordering (matches UDG exactly):
 *   - `loadState(state, update)` — runs the OLD state's onExit, swaps
 *     pointer, runs the NEW state's onEnter. Same-state calls no-op.
 *   - `updateState(update)`     — runs the current state's onUpdate.
 *
 * Either entry point is async; callers `await` so animation chains
 * complete before the next update is processed (TaskPool serialization
 * upstream guarantees we never overlap).
 */

import type { GameUpdate } from '@sdl/shared';
import type { State } from './State.js';

export class StateMachine {
  private states: Map<string, State> | null = null;
  private currentStateId: string | null = null;

  /**
   * Resolves on the first processed update of the current session. The
   * loading screen awaits this so a joining player doesn't see an empty
   * scene before onEnter has rendered the current state's UI. Call
   * `reset()` at the start of each new game to rebuild the barrier.
   */
  firstUpdateProcessed: Promise<void>;
  private _firstUpdateResolve: (() => void) | null = null;

  constructor() {
    this.firstUpdateProcessed = new Promise<void>((resolve) => {
      this._firstUpdateResolve = resolve;
    });
  }

  /**
   * Install the state map for the current game mode. Called once per
   * session as soon as the first GameUpdate arrives (when gameType is
   * first known). Subsequent calls replace the map (used on re-join).
   */
  registerStates(states: Map<string, State>): void {
    this.states = states;
  }

  hasStates(): boolean {
    return this.states !== null;
  }

  get currentState(): string | null {
    return this.currentStateId;
  }

  /**
   * Reset for a new game session. Clears the current pointer (so the
   * next update is treated as a fresh transition and fires onEnter of
   * whichever state the server places us in) and rebuilds the
   * `firstUpdateProcessed` barrier. The state map stays registered —
   * re-joining the same game type doesn't need a new build.
   */
  reset(): void {
    this.currentStateId = null;
    this.firstUpdateProcessed = new Promise<void>((resolve) => {
      this._firstUpdateResolve = resolve;
    });
  }

  /**
   * Process a GameUpdate. If the update's `gameState` differs from the
   * current pointer, runs onExit (old) then onEnter (new). Otherwise,
   * runs onUpdate. Always resolves the first-update barrier on
   * completion.
   */
  async process(update: GameUpdate): Promise<void> {
    if (!this.states) {
      console.warn('[StateMachine] process() before registerStates — dropping update', {
        version: update.version,
        gameState: update.gameState,
      });
      return;
    }

    const nextStateId = update.gameState;

    if (nextStateId !== this.currentStateId) {
      const oldState = this.currentStateId ? this.states.get(this.currentStateId) : null;
      const newState = this.states.get(nextStateId);

      if (oldState) {
        await oldState.onExit(update);
      }
      this.currentStateId = nextStateId;
      if (newState) {
        await newState.onEnter(update);
      } else {
        console.warn('[StateMachine] no handler for state', nextStateId);
      }
    } else {
      const handler = this.states.get(nextStateId);
      if (handler) await handler.onUpdate(update);
    }

    if (this._firstUpdateResolve) {
      this._firstUpdateResolve();
      this._firstUpdateResolve = null;
    }
  }
}
